import { SEARCH_DELAY } from '@aibees/elephant/feature/search-filters/constants';
import { useTrackEvents } from '@aibees/elephant/hooks';
import { useSearchQueryFilter } from '@aibees/elephant/hooks/use-search-query-filter';
import {
  DateRangePicker,
  DateRangePickerTuple,
  DateRangePickerValue,
} from '@aibees/shared/components';
import { useDebounceFn } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { Box, Card, CardContent, CardHeader, Typography } from '@mui/material';
import { memo } from 'react';
import { useRangeValueFromSearchUrl } from '../../hooks/use-range-value-from-search-url';
import { buildDraft } from '../number-range-filter/utils';
import type { DateRangePickerFilterProps as Props } from './types';

export const DateRangePickerFilter = memo(
  ({ className, field, name, open, type, nested, metadata }: Props) => {
    const { setParams, params } = useSearchQueryFilter({
      select: [field!],
    });
    const { track } = useTrackEvents();
    const value = useRangeValueFromSearchUrl<DateRangePickerTuple>(
      params[field!],
      type
    );

    const handleChangeStart = useDebounceFn(
      (e: DateRangePickerValue | undefined) => {
        const draft = buildDraft(
          params,
          type,
          field!,
          'add',
          0,
          e?.valueOf().toString()
        );
        setParams({ [field!]: draft });
        track('FILTER', {
          filterName: name,
          filterType: type,
          filterValue: draft,
          searchType: type,
        });
      },
      SEARCH_DELAY
    );

    const handleChangeEnd = useDebounceFn(
      (e: DateRangePickerValue | undefined) => {
        const draft = buildDraft(
          params,
          type,
          field!,
          'add',
          1,
          e?.valueOf().toString()
        );
        setParams({ [field!]: draft });
        track('FILTER', {
          filterName: name,
          filterType: type,
          filterValue: draft,
        });
      },
      SEARCH_DELAY
    );

    if (!open) return null;

    const content = (
      <DateRangePicker
        value={value}
        onChangeStart={handleChangeStart}
        onChangeEnd={handleChangeEnd}
      />
    );

    if (nested)
      return (
        <Box
          display="flex"
          flexDirection="column"
          gap={0.5}
          sx={metadata?.styles?.sx}
          className={classnames('DateRangePickerFilter', className)}
        >
          <Typography variant="body4" color="grey.500">
            {name}
          </Typography>
          {content}
        </Box>
      );

    return (
      <Card
        className={classnames('NumberRangeFilter', className)}
        variant="filled"
        color="default"
        sx={metadata?.styles?.sx}
      >
        {!!name && (
          <CardHeader
            title={
              <Typography variant="body4" color="grey.600" fontWeight={500}>
                {name}
              </Typography>
            }
          />
        )}
        <CardContent>{content}</CardContent>
      </Card>
    );
  }
);

DateRangePickerFilter.displayName = 'DateRangePickerFilter';
